import { ColumnDef } from '@tanstack/react-table';
import { Avatar } from '../../../../design-system/avatar/avatar/avatar';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { Input } from '../../../../design-system/input/input';
import { EmployeeView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { getCurrencySymbol } from '../../../../utils/get-currency-symbol';

export type PayrollEmployee = EmployeeView & {
  netPay: string;
  reference: string;
};

export const financialInformationTableColumnDefinition: ColumnDef<PayrollEmployee>[] = [
  {
    id: 'avatar',
    header: () => null,
    cell: ({ row }) => {
      const employee = row.original;
      const fullName = `${employee.firstName} ${employee.lastName}`;
      return <Avatar name={fullName} size="sm" />;
    },
    enableSorting: false,
    enableHiding: false,
    size: 6,
    minSize: 6,
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="First Name" />,
    cell: ({ row }) => <span className="text-sm">{row.original.firstName}</span>,
    size: 18,
    minSize: 18,
  },
  {
    accessorKey: 'lastName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Last Name" />,
    cell: ({ row }) => <span className="text-sm">{row.original.lastName}</span>,
    size: 18,
    minSize: 18,
  },
  {
    id: 'iban',
    header: ({ column, table }) => {
      if (table.options.meta?.showAccountOptions) return null;
      return <ColumnHeaderWithSorting column={column} label="IBAN" />;
    },
    cell: ({ row, table }) => {
      if (table.options.meta?.showAccountOptions) return null;
      return <span className="text-sm">{row.original.destination?.iban}</span>;
    },
    accessorFn: (row) => row.destination?.iban,
    size: 21,
  },
  {
    id: 'accountNumber',
    header: ({ column, table }) => {
      if (!table.options.meta?.showAccountOptions) return null;
      return <ColumnHeaderWithSorting column={column} label="Account Number" />;
    },
    cell: ({ row, table }) => {
      if (!table.options.meta?.showAccountOptions) return null;
      return <span className="text-sm">{row.original.destination?.accountNumber}</span>;
    },
    accessorFn: (row) => row.destination?.accountNumber,
    size: 16,
    minSize: 16,
  },
  {
    id: 'sortCode',
    header: ({ column, table }) => {
      if (!table.options.meta?.showAccountOptions) return null;
      return <ColumnHeaderWithSorting column={column} label="Sort Code" />;
    },
    cell: ({ row, table }) => {
      if (!table.options.meta?.showAccountOptions) return null;
      return <span className="text-sm">{row.original.destination?.sortCode}</span>;
    },
    accessorFn: (row) => row.destination?.sortCode,
    size: 10,
    minSize: 10,
  },
  {
    id: 'reference',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Reference" />,
    cell: ({ row }) => {
      return <span className="text-sm">{row.original.reference}</span>;
    },
    accessorKey: 'reference',
    size: 16,
    minSize: 16,
  },
  {
    id: 'netPay',
    header: ({ column }) => (
      <div className="flex justify-end">
        <ColumnHeaderWithSorting column={column} label="Net Pay" />
      </div>
    ),
    cell: ({ row, table }) => {
      const employee = row.original;
      const currency = employee.destination?.currency;
      const currencySymbol = getCurrencySymbol(currency);
      const onNetPayChange = table.options.meta?.onNetPayChange;

      return (
        <Input
          type="text"
          value={employee.netPay ? `${currencySymbol}${employee.netPay}` : ''}
          onChange={(e) => {
            const value = e.target.value;
            // Remove currency symbol and non-numeric chars, then validate
            const stringValue = value.replace(currencySymbol, '').replace(/[^\d.]/g, '');

            // Allow empty string or valid decimal numbers with up to 2 decimal places
            if (stringValue === '' || /^\d*\.?\d{0,2}$/.test(stringValue)) {
              if (onNetPayChange) {
                onNetPayChange(employee.id, stringValue);
              }
            }
          }}
          onFocus={(e) => {
            if (e.target.value === currencySymbol + '0') {
              e.target.value = currencySymbol;
            }
          }}
          placeholder={`${currencySymbol}0.00`}
          className={cn(
            'w-full rounded-lg border-seapoint-gray-border bg-white text-right text-sm',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          )}
          // Prevent row click when clicking on input
          onClick={(e) => e.stopPropagation()}
        />
      );
    },
    accessorKey: 'netPay',
    size: 13,
    minSize: 13,
  },
];
